import { ApplicationInsights, ICustomProperties, IEventTelemetry, IExceptionTelemetry, IMetricTelemetry } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import AppEnvironment from '../../environment/app-environment';
import { consoleLogger } from './service-console-logger';

class LogEvent {
  public eventTelemetry: IEventTelemetry;

  public customProperties?: ICustomProperties;

  public constructor(logEvent: LogEvent) {
    this.eventTelemetry = logEvent.eventTelemetry;
    this.customProperties = logEvent.customProperties;
  }
}

class ErrorEvent {
  public exceptionTelemetry: IExceptionTelemetry;

  public constructor(errorEvent: ErrorEvent) {
    this.exceptionTelemetry = errorEvent.exceptionTelemetry;
  }
}

class MetricEvent {
  public metricTelemetry: IMetricTelemetry;

  public customProperties?: ICustomProperties;

  public constructor(metricEvent: MetricEvent) {
    this.metricTelemetry = metricEvent.metricTelemetry;
  }
}

class LoggerService {
  private static instance: LoggerService;

  public appInsights: ApplicationInsights | undefined;

  public reactPlugin: ReactPlugin | undefined;

  private eventQueue: Array<LogEvent | ErrorEvent | MetricEvent> = []; // This will save logging events that happen while AI is loading. Then they will be executed after AI loads.

  public static get Instance() {
    if (!this.instance) {
      this.instance = new this();
    }
    return this.instance;
  }

  public init() {
    this.reactPlugin = new ReactPlugin();
    this.appInsights = new ApplicationInsights({
      config: {
        connectionString: `${AppEnvironment.appInsightsConnectionString}`,
        enableAutoRouteTracking: true,
        enableCorsCorrelation: true,
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        correlationHeaderExcludedDomains: ['*.queue.core.windows.net', '*psxsupportstore.azureedge.net'],
        correlationHeaderDomains: ['*.mint.microsoft.com'],
        extensions: [this.reactPlugin],
      },
    });
    this.appInsights.loadAppInsights();

    this.eventQueue.forEach((event) => {
      if (event instanceof LogEvent) {
        return this.log(event.eventTelemetry, event.customProperties);
      }

      if (event instanceof ErrorEvent) {
        return this.error(event.exceptionTelemetry);
      }
      return event;
    });
  }

  public cleanup() {
    if (this.appInsights) {
      this.appInsights.flush();
      this.appInsights = undefined;
      this.reactPlugin = undefined;
    }
    this.eventQueue = [];
  }

  public setUserContext(userId: string) {
    this.appInsights?.context.user.setAuthenticatedUserContext(userId);
  }

  public log(eventTelemetry: IEventTelemetry, customProperties?: ICustomProperties): any {
    if (AppEnvironment.consoleLogging) {
      consoleLogger.log(`${!this.appInsights ? 'QUEUED ' : ''}LoggerService.log`, {
        eventTelemetry,
        customProperties,
      });
    }

    if (!this.appInsights) {
      return this.eventQueue.push(new LogEvent({ eventTelemetry, customProperties }));
    }

    return this.appInsights.trackEvent(eventTelemetry, customProperties);
  }

  public error(exceptionTelemetry: IExceptionTelemetry) {
    if (AppEnvironment.consoleLogging) {
      consoleLogger.log(`${!this.appInsights ? 'QUEUED ' : ''}  Service.error`, {
        exceptionTelemetry,
      });
    }

    if (!this.appInsights) {
      return this.eventQueue.push(new ErrorEvent({ exceptionTelemetry }));
    }

    return this.appInsights.trackException(exceptionTelemetry);
  }

  public logMetric(metricTelemetry: IMetricTelemetry, customProperties?: ICustomProperties) {
    if (AppEnvironment.consoleLogging) {
      consoleLogger.log(`${!this.appInsights ? 'QUEUED ' : ''}  Service.logMetric`, {
        metricTelemetry,
        customProperties,
      });
    }

    if (!this.appInsights) {
      return this.eventQueue.push(new MetricEvent({ metricTelemetry, customProperties }));
    }

    return this.appInsights.trackMetric(metricTelemetry, customProperties);
  }
}

export default LoggerService.Instance;
